import type { ApproverRow } from '@clockwise/clients/approval-instances';
import { ApiError, userDisplayName } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { approvalInstancesApi, usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { approvalResolutionText } from '../approval-resolution-text';

// Covers every approval-instance status (pending · in_progress · approved ·
// rejected · failed · canceled) and approver status. Unknown values fall back
// to plain text via the `?? ''` at the call sites.
const STATUS_STYLES: Record<string, string> = {
  approved: 'text-emerald-700 dark:text-emerald-300',
  rejected: 'text-red-700 dark:text-red-300',
  failed: 'text-red-700 dark:text-red-300',
  canceled: 'text-muted-foreground',
  pending: 'text-amber-700 dark:text-amber-300',
  in_progress: 'text-amber-700 dark:text-amber-300',
};

/**
 * Read-only approval timeline for a timesheet (or any approvals-tracked
 * object): the instance outcome plus each approver's decision, who and when.
 * Fetched lazily — only mounted when an audit row is expanded — via the
 * existing `GET /api/approval-instances/:objectType/:objectId`.
 */
export function ApprovalTimeline({
  objectType,
  objectId,
}: {
  objectType: string;
  objectId: string;
}) {
  const query = useQuery({
    queryKey: queryKeys.approvalInstances.forObject(objectType, objectId),
    queryFn: () => approvalInstancesApi.getForObject(objectType, objectId),
  });

  const approvers = query.data?.related?.approvers ?? [];
  const instance = query.data?.data ?? null;

  // Resolve approver + submitter ids to names in one batched lookup.
  const userIds = useMemo(() => {
    const ids = new Set<string>();
    if (instance?.submittedBy) ids.add(instance.submittedBy);
    for (const a of approvers) ids.add(a.userId);
    return [...ids];
  }, [instance?.submittedBy, approvers]);

  const namesQuery = useQuery({
    queryKey: queryKeys.users.byIds(userIds),
    queryFn: () => usersApi.findByIds(userIds),
    enabled: userIds.length > 0,
  });
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of namesQuery.data?.data ?? []) {
      m.set(u.id, userDisplayName(u));
    }
    return m;
  }, [namesQuery.data]);
  const nameOf = (id: string): string => nameById.get(id) ?? 'Unknown user';

  if (query.isLoading) {
    return (
      <div className="ml-7 mt-2 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-56" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="ml-7 mt-2 text-xs text-destructive">
        {query.error instanceof ApiError ? query.error.message : 'Failed to load approvals.'}
      </p>
    );
  }
  if (!instance) {
    return <p className="ml-7 mt-2 text-xs text-muted-foreground">No approval record.</p>;
  }

  return (
    <div className="ml-7 mt-2 rounded-md border p-3 text-xs">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">Approval</span>
        <span className={cn('font-medium', STATUS_STYLES[instance.status] ?? '')}>
          {instance.status}
        </span>
        <span className="text-muted-foreground">
          · submitted by {nameOf(instance.submittedBy)}
          {instance.resolvedAt
            ? ` · resolved ${new Date(instance.resolvedAt).toLocaleString()}`
            : ''}
        </span>
      </div>
      {instance.autoApproved ? (
        <p className="text-muted-foreground">Auto-approved by policy.</p>
      ) : approvers.length === 0 ? (
        <p className="text-muted-foreground">No approver decisions recorded.</p>
      ) : (
        <ul className="space-y-1">
          {approvers.map((a: ApproverRow) => (
            <li key={a.id} className="flex flex-wrap items-center gap-x-2">
              <span className="font-medium">{nameOf(a.userId)}</span>
              <span className={cn(STATUS_STYLES[a.status] ?? '')}>{a.status}</span>
              {a.decidedAt ? (
                <span className="text-muted-foreground">
                  {new Date(a.decidedAt).toLocaleString()}
                </span>
              ) : null}
              {a.reason ? <span className="text-muted-foreground">— {a.reason}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {approvalResolutionText(instance.resolutionReason) ? (
        <p className="mt-2 text-muted-foreground">
          Reason: {approvalResolutionText(instance.resolutionReason)}
        </p>
      ) : null}
    </div>
  );
}
